/**
 * DealFormDialog - Modal premium para criar/editar Deals
 * Layout alinhado com CreateChargeModal: seções com ícone+título+separador
 * Inclui funcionalidade de adicionar produtos/serviços na criação
 *
 * Versão: 3.10.0
 * - v3.10.0 (2026-10-18): prop `defaultVehicles` pré-seleciona veículos na criação
 *           (ex.: aba Negócios da ficha do veículo).
 * - v3.9.0: Bloqueio do Workflow na proposta automática usa aviso modal global.
 * - v3.8.0: A seção "Veículos" só é renderizada quando o módulo Veículos está
 *           habilitado no tenant (flag `vehicles_enabled` via useVehiclesModuleEnabled).
 * - v3.7.0: Seleção do "Tipo de proposta" no bloco de proposta automática

 *           (pré-seleciona quando existe apenas um tipo ativo, ex.: Nova venda).
 * - v3.6.0: "Ver negócio" no modal de sucesso agora abre o DealDetailsModalPremium
 *           montado aqui mesmo (padrão do sistema) em vez de navegar para a página
 *           /tenant/crm/deals/:id. Telas que informam `onViewDeal` seguem inalteradas.
 * - v3.5.0: O modal de sucesso (DealCreatedSuccessDialog) passou a ser montado aqui,
 *           garantindo que ele apareça após criar um negócio em QUALQUER tela
 *           (botão "+" do header, chat, dashboard, modal de cliente, veículo...).
 *           Props novas: `showSuccessDialog` (padrão true) e `onViewDeal`.

 * - v3.4.0: Correção — os chips de veículos eram limpos logo após a seleção (efeito de
 *           sincronização disparava a cada render). Agora só limpa ao abrir em modo criação.
 *           Novo: respeita a configuração do tenant "Restringir seleção aos veículos do cliente".
 * - v3.3.0: Botão "+" na seção Veículos para cadastrar um novo veículo sem sair
 *           do modal (visível apenas com permissão de criação em vehicles_list)
 * - v3.1.0: Seção "Veículos" para vincular/desvincular veículos no criar/editar
 * - Layout redesenhado (padrão CreateChargeModal)
 * - Seção de Produtos/Serviços com tabela, descontos e totalizadores
 * - Inserção de itens em crm_deal_items após criação do deal
 */

import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { formatDealCode } from '@/lib/formatters';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useCrmDeals, CrmDeal, CreateDealInput } from '@/hooks/useCrmDeals';
import { useCurrentUserSeller } from '@/hooks/useCurrentUserSeller';
import { useCrmProducts } from '@/hooks/useCrmProducts';
// v3.2.0: criação automática de proposta (reaproveita o módulo de propostas)
import { useCreateProposalWithLink } from '@/hooks/useDealProposals';
import { useWorkflowGuardNotice } from '@/providers/WorkflowGuardNoticeProvider';
// v3.7.0: tipos de proposta cadastrados pelo tenant (padrão "Nova venda")
import { useCrmProposalTypes } from '@/hooks/useCrmProposalTypes';
import { Checkbox } from '@/components/ui/checkbox';
import { ContactSelector } from '@/components/crm/ContactSelector';
// v3.4.0: configuração do tenant que restringe a seleção de veículos ao cliente/contato
// v3.8.0: `useVehiclesModuleEnabled` — flag `vehicles_enabled` do tenant
import { useVehiclesModule, useVehiclesModuleEnabled } from '@/hooks/useVehiclesModule';

import { SellerSelector } from '@/components/crm/SellerSelector';
import { UserSelector } from '@/components/crm/UserSelector';
import { PhaseSelector } from '@/components/crm/PhaseSelector';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { 
  Briefcase, 
  Users, 
  ChevronDown,
  Settings2,
  Calendar,
  Percent,
  Globe,
  Plus,
  User,
  Layers,
  Package,
  ShoppingCart,
  Calculator,
  Trash2,
  Loader2,
  FileText,
  Upload,
  Paperclip,
  X,
  File,
  Image as ImageIcon,
  Car,
} from 'lucide-react';
// v3.1.0: vínculo de veículos no formulário de negócio
import { VehicleSelector } from '@/components/tasks/VehicleSelector';
// v3.3.0: cadastro rápido de veículo dentro do modal de negócio
import { VehicleFormDialog } from '@/components/vehicles/VehicleFormDialog';
// v3.3.0: permissões por perfil (admin tem bypass no contexto)
import { usePermissionsContext } from '@/contexts/PermissionsContext';
import { useCustomerVehicles, useDealVehicleLink } from '@/hooks/useCustomerVehicles';
import { Button } from '@/components/ui/button';
import { CreateContactDialog } from '@/components/tenant/contacts/CreateContactDialog';
import { cn } from '@/lib/utils';

// Componentes reutilizados
import { PrioritySelector } from './components/PrioritySelector';
import { ProductCatalogModal } from './modal/tabs/products/ProductCatalogModal';
import { InlineEditableField } from './modal/tabs/products/InlineEditableField';
// Reaproveita o componente Premium da aba Produtos do modal de detalhes do deal
import { DealItemsSummary } from './modal/tabs/products/DealItemsSummary';
import { InlineNotice } from '@/components/ui/inline-notice';
// Trava de edição de itens quando existe proposta aceita
import { useDealHasAcceptedProposal } from '@/hooks/useDealAcceptedProposal';
import type { RecurrenceInterval, TermType, TermUnit } from '@/lib/crm/recurringServiceUtils';
import { formatCurrency } from '@/lib/currency-utils';
import { createLogger } from '@/lib/logger';
import { useDealAttachments } from '@/hooks/useDealAttachments';
import { getStorageUrl } from '@/lib/storage';
import { toast } from 'sonner';
// v3.5.0: modal de sucesso exibido após criar o negócio (em qualquer tela)
import { DealCreatedSuccessDialog } from './modal/DealCreatedSuccessDialog';
// v3.6.0: modal padrão de detalhes do negócio (substitui a navegação para a página)
import { DealDetailsModalPremium } from './modal/DealDetailsModalPremium';




// Constantes de origens de lead e ícones
import { getLeadSourceIcon } from '@/lib/lead-sources';
import { useLeadSources } from '@/hooks/useLeadSources';

// Logger do módulo
const logger = createLogger('DealFormDialog');

// Schema de validação
// v2.1.0 - Campo "value" removido do schema. O valor do negócio é calculado 
// automaticamente a partir dos produtos/serviços via sync_deal_value_from_items
const dealSchema = z.object({
  workspace_id: z.string().min(1, 'Workspace é obrigatório'),
  title: z.string().min(1, 'Título é obrigatório'),
  pipeline_id: z.string().min(1, 'Pipeline é obrigatório'),
  phase_id: z.string().min(1, 'Fase é obrigatória'),
  contact_id: z.string().min(1, 'Contato é obrigatório'),
  company_name: z.string().optional(),
  currency: z.string().default('BRL'),
  priority: z.enum(['low', 'medium', 'high', 'urgent'] as const),
  expected_close_date: z.string().optional(),
  probability: z.number().min(0).max(100).optional(),
  source: z.string().min(1, 'Origem é obrigatória'),
  description: z.string().optional(),
  assigned_to: z.string().nullable().optional(),
  seller_id: z.string().nullable().optional(),
});

type DealFormData = z.infer<typeof dealSchema>;

/** Modo de desconto: percentual ou valor fixo */
type DiscountMode = 'percent' | 'fixed';

/** Item interno do formulário (com product_id e dados do catálogo) */
interface DealFormItem {
  id: string; // ID temporário para key do React
  product_id: string;
  product_name: string;
  product_sku: string | null;
  quantidade: number;
  valor_unitario: number;
  desconto_percent: number; // Desconto em percentual (0-100)
  desconto_fixed: number;   // Desconto em valor fixo
  // Metadados do catálogo — necessários para o DealItemsSummary
  // separar Produtos x Serviços e exibir Mensalidade/Prazo nos recorrentes
  product_type?: 'product' | 'service';
  unit_type?: string;
  recurring_price?: number | null;
  recurrence_interval?: RecurrenceInterval | null;
  term_type?: TermType | null;
  term_quantity?: number | null;
  term_unit?: TermUnit | null;
}

/**
 * Toggle compacto para alternar entre modo % e R$
 * Replica o mesmo padrão visual do CreateChargeModal
 */
function DiscountModeToggle({
  mode,
  onToggle,
}: {
  mode: DiscountMode;
  onToggle: (newMode: DiscountMode) => void;
}) {
  return (
    <div className="inline-flex rounded-md border border-border/60 overflow-hidden h-6">
      <button
        type="button"
        onClick={() => onToggle('percent')}
        className={cn(
          'px-1.5 text-[10px] font-medium transition-colors',
          mode === 'percent'
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted/30 text-muted-foreground hover:bg-muted/60'
        )}
        title="Desconto em percentual"
      >
        %
      </button>
      <button
        type="button"
        onClick={() => onToggle('fixed')}
        className={cn(
          'px-1.5 text-[10px] font-medium transition-colors',
          mode === 'fixed'
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted/30 text-muted-foreground hover:bg-muted/60'
        )}
        title="Desconto em valor fixo"
      >
        R$
      </button>
    </div>
  );
}

// Props do componente
interface DealFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deal?: CrmDeal | null;
  defaultPipelineId?: string;
  defaultPhaseId?: string;
  defaultContactId?: string;
  defaultWorkspaceId?: string;
  /** v3.10.0: veículos já selecionados ao abrir em modo criação */
  defaultVehicles?: Array<{ id: string; label: string }>;
  /**
   * Callback de sucesso - recebe dados do deal criado (opcional).
   * v3.2.0: `proposal` vem preenchido quando a proposta foi gerada
   * automaticamente junto com o negócio.
   */
  onSuccess?: (newDeal?: {
    id: string;
    title: string;
    proposal?: { id: string; publicUrl: string | null } | null;
  }) => void;
  /**
   * v3.5.0: exibe o modal de sucesso após a criação (padrão: true).
   * Só desligar quando a tela já mostrar um retorno equivalente.
   */
  showSuccessDialog?: boolean;
  /**
   * v3.5.0: como abrir o negócio ao clicar em "Visualizar Negócio".
   * Sem esta prop, navega para a página do negócio (/tenant/crm/deals/:id).
   */
  onViewDeal?: (dealId: string) => void;
}

/**
 * Modal premium para criar/editar Deal
 * Layout alinhado com o padrão do CreateChargeModal
 */
export function DealFormDialog({
  open,
  onOpenChange,
  deal,
  defaultPipelineId,
  defaultPhaseId,
  defaultContactId,
  defaultWorkspaceId,
  defaultVehicles,
  onSuccess,
  showSuccessDialog = true,
  onViewDeal,
}: DealFormDialogProps) {
  const { currentTenant } = useAuth();
  const { createDeal, updateDeal } = useCrmDeals(undefined, { enabled: false }); // Apenas mutations — evita SELECT * desnecessário
  const queryClient = useQueryClient();
  const workflowNotice = useWorkflowGuardNotice();
  // v3.6.0: a navegação para a página do negócio foi removida (usa-se o modal padrão)
  const isEditing = !!deal;

  /**
   * v3.5.0 — Negócio recém-criado que alimenta o modal de sucesso.
   * Mantido em estado próprio para sobreviver ao fechamento do formulário.
   */
  const [createdDeal, setCreatedDeal] = useState<{
    id: string;
    title: string;
    proposal?: { id: string; publicUrl: string | null } | null;
  } | null>(null);
  const [successOpen, setSuccessOpen] = useState(false);
  /**
   * v3.6.0 — Negócio a ser exibido no modal padrão de detalhes após "Ver negócio".
   * Mantém o usuário na tela atual (nada de navegar para /tenant/crm/deals/:id).
   */
  const [viewDealId, setViewDealId] = useState<string | null>(null);



  // Catálogo dinâmico de origens (sistema + custom do tenant)
  const { activeSources: leadSources } = useLeadSources();
  
  // Busca seller vinculado ao usuário logado (para pré-selecionar ao criar negócio)
  const { seller: currentUserSeller } = useCurrentUserSeller();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [createContactOpen, setCreateContactOpen] = useState(false);

  // === Estados de Produtos/Serviços ===
  const [itens, setItens] = useState<DealFormItem[]>([]);
  const [discountModes, setDiscountModes] = useState<Record<string, DiscountMode>>({});
  const [catalogModalOpen, setCatalogModalOpen] = useState(false);
  // v3.2.0: gerar proposta automaticamente ao criar o negócio (somente com itens)
  const [autoCreateProposal, setAutoCreateProposal] = useState(true);
  // Helper que cria a proposta e já ativa o link público (reuso do módulo de propostas)
  const { createWithLink } = useCreateProposalWithLink();
  // v3.7.0: tipo da proposta gerada automaticamente (obrigatório no servidor)
  const { activeTypes: proposalTypes } = useCrmProposalTypes();
  const [proposalTypeId, setProposalTypeId] = useState<string>('');
  // Pré-seleciona o primeiro tipo ativo (normalmente "Nova venda") assim que carregar
  useEffect(() => {
    if (!proposalTypeId && proposalTypes.length > 0) {
      setProposalTypeId(proposalTypes[0].id);
    }
  }, [proposalTypes, proposalTypeId]);

  // === Estados de Anexos pendentes (upload após criação do deal) ===
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // === v3.1.0: Veículos vinculados ao negócio (chips locais + persistência no submit) ===
  const [vehicles, setVehicles] = useState<Array<{ id: string; label: string }>>([]);
  const linkDealVehicle = useDealVehicleLink();
  const { vehicles: linkedDealVehicles } = useCustomerVehicles(
    { dealId: open && isEditing && deal ? deal.id : null },
    open && isEditing && !!deal,
  );

  // v3.2.0 (2026-08-12): negócio com proposta ACEITA tem produtos/serviços congelados
  const { hasAcceptedProposal } = useDealHasAcceptedProposal(
    open && isEditing && deal ? deal.id : null,
    open && isEditing && !!deal,
  );



  /**
   * Sincroniza os chips com os vínculos existentes (edição).
   *
   * v3.4.0: na criação a limpeza acontece apenas quando o modal abre. Antes o efeito
   * dependia de `linkedDealVehicles`, que era um array novo a cada render (query
   * desabilitada), zerando a seleção logo após o usuário escolher um veículo.
   */
  useEffect(() => {
    if (!open) return;
    if (isEditing && deal) {
      setVehicles(
        linkedDealVehicles.map((v) => ({
          id: v.id,
          label: [v.plate, v.brand_name, v.model_name].filter(Boolean).join(' · ') || 'Veículo',
        })),
      );
    }
  }, [open, isEditing, deal, linkedDealVehicles]);

  /** v3.4.0: limpa a seleção somente ao abrir o modal em modo criação */
  useEffect(() => {
    // v3.10.0: inicia com os veículos padrão (quando informados)
    if (open && !isEditing) setVehicles(defaultVehicles ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só ao abrir
  }, [open, isEditing]);

  // Busca produtos do catálogo para resolver nomes
  const { products } = useCrmProducts();

  // ======== FORM ========
  
  const form = useForm<DealFormData>({
    resolver: zodResolver(dealSchema),
    defaultValues: {
      workspace_id: defaultWorkspaceId || '',
      title: '',
      pipeline_id: defaultPipelineId || '',
      phase_id: defaultPhaseId || '',
      contact_id: '',
      company_name: '',
      currency: 'BRL',
      priority: 'medium',
      expected_close_date: '',
      probability: undefined,
      source: '',
      description: '',
      assigned_to: null,
      seller_id: null,
    },
  });

  // Watch do workspace selecionado no form (para filtrar pipelines)
  const selectedWorkspaceId = form.watch('workspace_id');
  const selectedPipelineId = form.watch('pipeline_id');
  const isSubmitting = form.formState.isSubmitting;

  // === v3.3.0: cadastro rápido de veículo (botão "+" da seção Veículos) ===
  // Permissão de criação por perfil; admin tem bypass dentro do contexto.
  const { canCreate: canCreateMenu, isAdmin: isTenantAdmin } = usePermissionsContext();
  const canCreateVehicle = isTenantAdmin || canCreateMenu('vehicles_list');

  // Contato selecionado no formulário (dono do veículo criado)
  const selectedContactId = form.watch('contact_id');
  // v3.4.0: cliente do contato selecionado (usado no escopo da listagem de veículos)
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  // v3.4.0: flag do tenant — lista apenas veículos do cliente/contato
  const { scopeToCustomer: vehiclesScopeToCustomer } = useVehiclesModule();
  // v3.8.0: módulo Veículos habilitado no tenant (esconde a seção quando desligado)
  const vehiclesModuleEnabled = useVehiclesModuleEnabled();

  const [vehicleFormOpen, setVehicleFormOpen] = useState(false);
  // ID do veículo recém-criado aguardando resolução do rótulo para virar chip
  const [pendingNewVehicleId, setPendingNewVehicleId] = useState<string | null>(null);

  // v3.8.0: último workspace conhecido (evita limpar pipeline/fase na abertura)
  const prevWorkspaceIdRef = useRef<string>('');

  // Lista de veículos do contato — usada apenas para resolver o rótulo do novo veículo
  const { vehicles: contactVehicles } = useCustomerVehicles(
    { contactId: selectedContactId || null },
    open && !!selectedContactId && !!pendingNewVehicleId,
  );

  // Quando o veículo recém-criado aparecer na lista do contato, vira chip local
  useEffect(() => {
    if (!pendingNewVehicleId) return;
    const found = contactVehicles.find((v) => v.id === pendingNewVehicleId);
    if (!found) return;
    const label =
      [found.plate, found.brand_name, found.model_name].filter(Boolean).join(' · ') || 'Veículo';
    setVehicles((prev) =>
      prev.some((v) => v.id === found.id) ? prev : [...prev, { id: found.id, label }],
    );
    setPendingNewVehicleId(null);
  }, [pendingNewVehicleId, contactVehicles]);


  // ======== WORKSPACE E PIPELINE ========
  
  // Busca workspaces ativos do tenant
  const { data: workspaces = [] } = useQuery({
    queryKey: ['crm-workspaces-active', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      const { data, error } = await supabase
        .from('crm_workspaces')
        .select('id, name, color')
        .eq('tenant_id', currentTenant.id)
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return data;
    },
    enabled: !!currentTenant?.id && open,
  });

  // Busca pipelines do workspace selecionado
  const { data: pipelines = [] } = useQuery({
    queryKey: ['crm-pipelines-by-workspace', currentTenant?.id, selectedWorkspaceId],
    queryFn: async () => {
      if (!currentTenant?.id || !selectedWorkspaceId) return [];
      const { data, error } = await supabase
        .from('crm_pipelines')
        .select('id, name, workspace_id')
        .eq('tenant_id', currentTenant.id)
        .eq('workspace_id', selectedWorkspaceId)
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return data;
    },
    enabled: !!currentTenant?.id && !!selectedWorkspaceId && open,
  });

  // Busca membros ativos do tenant para atribuição como responsável
  const { data: users = [] } = useQuery({
    queryKey: ['tenant-members-for-deals', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      const { data: tenantMembers, error: tmError } = await supabase
        .from('tenant_members')
        .select(`
          user_id,
          profiles:user_id (
            id,
            full_name,
            email
          )
        `)
        .eq('tenant_id', currentTenant.id)
        .eq('is_active', true);
      
      if (tmError || !tenantMembers?.length) return [];
      return tenantMembers
        .filter((m: any) => m.profiles)
        .map((m: any) => ({
          id: m.profiles.id,
          name: m.profiles.full_name || m.profiles.email || 'Usuário',
        }));
    },
    enabled: !!currentTenant?.id && open,
  });

  // Limpa pipeline e fase quando workspace muda (para evitar seleção inválida)
  // v3.8.0: a limpeza só acontece quando o usuário REALMENTE troca o workspace.
  // Antes, a própria seleção inicial (vazio -> workspace padrão) disparava este
  // efeito logo após o form.reset, apagando pipeline/fase pré-preenchidos vindos
  // do botão "+" do cabeçalho da fase (só funcionava na 2ª abertura do modal).
  useEffect(() => {
    const anterior = prevWorkspaceIdRef.current;
    prevWorkspaceIdRef.current = selectedWorkspaceId || '';

    if (deal) return;
    if (!selectedWorkspaceId) return;
    if (!anterior || anterior === selectedWorkspaceId) return;

    form.setValue('pipeline_id', '');
    form.setValue('phase_id', '');
  }, [selectedWorkspaceId, deal]);

  // Re-afirma pipeline_id e phase_id após pipelines carregarem (modo edição)
  // Resolve problema do Radix Select não exibir valor quando items chegam após o value
  // Re-afirma pipeline_id e phase_id com técnica clear-then-set
  // Radix Select ignora setValue com mesmo valor — limpar e re-setar força reconhecimento
  useEffect(() => {
    if (!open || !deal || !hasInitializedRef.current) return;
    if (pipelines.length === 0) return;
    
    const currentPipeline = form.getValues('pipeline_id');
    if (currentPipeline && pipelines.some(p => p.id === currentPipeline)) {
      // Força Radix Select a reconhecer: limpa e re-seta no próximo frame
      form.setValue('pipeline_id', '', { shouldValidate: false });
      requestAnimationFrame(() => {
        form.setValue('pipeline_id', currentPipeline, { shouldValidate: false });
        
        // Re-afirma phase_id após pipeline ser re-setado
        const currentPhase = form.getValues('phase_id');
        if (currentPhase) {
          form.setValue('phase_id', '', { shouldValidate: false });
          requestAnimationFrame(() => {
            form.setValue('phase_id', currentPhase, { shouldValidate: false });
          });
        }
      });
    }
  }, [pipelines, open, deal]);

  // Re-afirma workspace_id após workspaces carregarem (modo edição)
  // Resolve race condition onde form.reset ocorre antes da lista de workspaces estar disponível
  useEffect(() => {
    if (!open || !deal || !hasInitializedRef.current) return;
    if (workspaces.length === 0) return;
    
    const currentWorkspace = form.getValues('workspace_id');
    if (currentWorkspace && workspaces.some(w => w.id === currentWorkspace)) {
      // Força re-set para sincronizar Radix Select com items já disponíveis
      form.setValue('workspace_id', currentWorkspace, { shouldValidate: false });
    }
  }, [workspaces, open, deal]);

  // Busca workspace_id da pipeline do deal (para edição)
  const { data: dealWorkspaceId } = useQuery({
    queryKey: ['deal-pipeline-workspace', deal?.pipeline_id],
    queryFn: async () => {
      if (!deal?.pipeline_id) return null;
      const { data, error } = await supabase
        .from('crm_pipelines')
        .select('workspace_id')
        .eq('id', deal.pipeline_id)
        .single();
      if (error) return null;
      return data?.workspace_id;
    },
    enabled: !!deal?.pipeline_id && open,
  });

  // Ref para evitar re-inicialização destrutiva quando deps assíncronas mudam
  const hasInitializedRef = useRef(false);

  // Preenche formulário APENAS na primeira abertura do modal (evita limpar dados já preenchidos)
  useEffect(() => {
    if (!open) {
      hasInitializedRef.current = false;
      // v3.8.0: reinicia o rastreio do workspace para a próxima abertura
      prevWorkspaceIdRef.current = '';
      return;
    }
    if (hasInitializedRef.current) return; // Já inicializou nesta sessão do modal
    // No modo edição, aguarda dealWorkspaceId carregar antes de inicializar o formulário
    // Aguarda workspaces E dealWorkspaceId carregarem antes de inicializar (evita race condition)
    if (deal && (!dealWorkspaceId || workspaces.length === 0)) return;
    hasInitializedRef.current = true;

    if (deal) {
      form.reset({
        workspace_id: dealWorkspaceId || '',
        title: deal.title,
        pipeline_id: deal.pipeline_id,
        phase_id: deal.phase_id,
        contact_id: deal.contact_id,
        company_name: deal.company_name || '',
        currency: deal.currency,
        priority: deal.priority,
        expected_close_date: deal.expected_close_date?.split('T')[0] || '',
        probability: deal.probability || undefined,
        source: deal.source || '',
        description: deal.description_html || deal.description || '',
        assigned_to: deal.assigned_to,
        seller_id: deal.seller_id || null,
      });
      if (deal.expected_close_date || deal.probability || deal.source || deal.description) {
        setDetailsOpen(true);
      }
      // Carregar itens existentes do deal para exibição no modo edição
      supabase
        .from('crm_deal_items')
        .select('*, product:crm_products(id, name, sku, product_type, unit_type, default_price, recurring_price, recurrence_interval, term_type, term_quantity, term_unit)')
        .eq('deal_id', deal.id)
        .order('sort_order', { ascending: true })
        .then(({ data: dealItems }) => {
          if (dealItems && dealItems.length > 0) {
            const loadedItens: DealFormItem[] = dealItems.map(item => ({
              id: item.id,
              product_id: item.product_id,
              product_name: item.product?.name || 'Produto',
              product_sku: item.product?.sku || null,
              quantidade: item.quantity,
              valor_unitario: item.unit_price,
              desconto_percent: item.discount_percent || 0,
              desconto_fixed: item.discount_fixed || 0,
              // Metadados do produto para o DealItemsSummary
              product_type: (item.product?.product_type as 'product' | 'service') || 'product',
              unit_type: item.product?.unit_type || undefined,
              recurring_price: (item.product?.recurring_price as number) ?? null,
              recurrence_interval: (item.product?.recurrence_interval as RecurrenceInterval) ?? null,
              term_type: (item.product?.term_type as TermType) ?? null,
              term_quantity: (item.product?.term_quantity as number) ?? null,
              term_unit: (item.product?.term_unit as TermUnit) ?? null,
            }));
            setItens(loadedItens);
            // Configurar modos de desconto baseado nos dados salvos
            const modes: Record<string, DiscountMode> = {};
            dealItems.forEach(item => {
              modes[item.id] = item.discount_fixed > 0 ? 'fixed' : 'percent';
            });
            setDiscountModes(modes);
          } else {
            setItens([]);
            setDiscountModes({});
          }
        });
    } else {
      form.reset({
        workspace_id: defaultWorkspaceId || '',
        title: '',
        pipeline_id: defaultPipelineId || '',
        phase_id: defaultPhaseId || '',
        contact_id: defaultContactId || '',
        company_name: '',
        currency: 'BRL',
        priority: 'medium',
        expected_close_date: '',
        probability: undefined,
        source: '',
        description: '',
        assigned_to: null,
        seller_id: currentUserSeller?.id || null,
      });
      setDetailsOpen(false);
      // Limpa itens e anexos ao abrir para criação
      setItens([]);
      setDiscountModes({});
      setPendingFiles([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, deal, dealWorkspaceId, defaultPipelineId, defaultPhaseId, defaultContactId, defaultWorkspaceId, workspaces]);

  // Efeito separado: pré-seleciona seller quando carrega assincronamente (sem limpar estado)
  useEffect(() => {
    if (!open || deal) return;
    const currentSeller = form.getValues('seller_id');
    if (!currentSeller && currentUserSeller?.id) {
      form.setValue('seller_id', currentUserSeller.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, deal, currentUserSeller?.id]);

  // Hook para buscar anexos existentes no modo edição
  const { 
    attachments: existingAttachments, 
    deleteAttachment, 
    isDeleting: isDeletingAttachment 
  } = useDealAttachments(isEditing && deal?.id ? deal.id : undefined);

  // === Handlers de Itens (Produtos/Serviços) ===

  /** Remover item da lista local */
  const handleRemoveItem = useCallback((itemId: string) => {
    setItens(prev => prev.filter(i => i.id !== itemId));
  }, []);

  /** Atualizar campo de um item */
  const handleUpdateItem = useCallback((itemId: string, field: keyof DealFormItem, value: number | string) => {
    setItens(prev =>
      prev.map(i => i.id === itemId ? { ...i, [field]: value } : i)
    );
  }, []);

  /** Calcula o desconto absoluto de um item com base no modo ativo */
  const calcItemDiscount = useCallback((item: DealFormItem) => {
    const mode = discountModes[item.id] || 'percent';
    const subtotal = item.quantidade * item.valor_unitario;
    if (mode === 'percent') {
      return subtotal * (item.desconto_percent / 100);
    }
    return item.desconto_fixed;
  }, [discountModes]);

  /** Formatar moeda BRL */
  const formatBRL = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

  // Cálculos totais
  const subtotalGeral = useMemo(
    () => itens.reduce((sum, item) => sum + item.quantidade * item.valor_unitario, 0),
    [itens]
  );

  const totalDescontos = useMemo(
    () => itens.reduce((sum, item) => sum + calcItemDiscount(item), 0),
    [itens, calcItemDiscount]
  );

  const valorTotal = useMemo(
    () => subtotalGeral - totalDescontos,
    [subtotalGeral, totalDescontos]
  );

  // === Adaptadores para ProductCatalogModal ===

  /** Itens no formato esperado pelo ProductCatalogModal */
  const catalogItemsForModal = useMemo(
    () => itens.map(i => ({ product_id: i.product_id, quantity: i.quantidade })),
    [itens]
  );

  /** Handler: adicionar item vindo do catálogo */
  const handleCatalogAddItem = useCallback(async (productId: string, quantity: number, unitPrice: number) => {
    const existing = itens.find(i => i.product_id === productId);
    if (existing) {
      setItens(prev =>
        prev.map(i => i.product_id === productId ? { ...i, quantidade: i.quantidade + quantity } : i)
      );
      return;
    }
    const product = products.find(p => p.id === productId);
    const novoItem: DealFormItem = {
      id: crypto.randomUUID(),
      product_id: productId,
      product_name: product?.name || 'Produto',
      product_sku: product?.sku || null,
      quantidade: quantity,
      valor_unitario: unitPrice,
      desconto_percent: 0,
      desconto_fixed: 0,
      // Metadados do catálogo para o DealItemsSummary (separação produto/serviço + recorrência)
      product_type: product?.product_type ?? 'product',
      unit_type: product?.unit_type,
      recurring_price: product?.recurring_price ?? null,
      recurrence_interval: product?.recurrence_interval ?? null,
      term_type: product?.term_type ?? null,
      term_quantity: product?.term_quantity ?? null,
      term_unit: product?.term_unit ?? null,
    };
    setItens(prev => [...prev, novoItem]);
  }, [itens, products]);

  /** Handler: atualizar quantidade vindo do catálogo */
  const handleCatalogUpdateQuantity = useCallback(async (productId: string, quantity: number) => {
    setItens(prev =>
      prev.map(i => i.product_id === productId ? { ...i, quantidade: quantity } : i)
    );
  }, []);

  /** Handler: remover item vindo do catálogo */
  const handleCatalogRemoveItem = useCallback(async (productId: string) => {
    setItens(prev => prev.filter(i => i.product_id !== productId));
  }, []);

  // === Adaptador: DealFormItem (estado local em PT) → DealItem (shape do banco) ===
  // Necessário para reaproveitar 100% o componente DealItemsSummary sem fork.
  const itensForSummary = useMemo(
    () =>
      itens.map((i) => {
        const subtotal = i.quantidade * i.valor_unitario;
        const discount = calcItemDiscount(i);
        return {
          id: i.id,
          product_id: i.product_id,
          quantity: i.quantidade,
          unit_price: i.valor_unitario,
          discount_percent: i.desconto_percent || null,
          discount_fixed: i.desconto_fixed || null,
          total: subtotal - discount,
          product: {
            id: i.product_id,
            name: i.product_name,
            sku: i.product_sku,
            product_type: i.product_type ?? 'product',
            unit_type: i.unit_type ?? 'un',
            recurring_price: i.recurring_price ?? null,
            recurrence_interval: i.recurrence_interval ?? null,
            term_type: i.term_type ?? null,
            term_quantity: i.term_quantity ?? null,
            term_unit: i.term_unit ?? null,
          },
        };
      }),
    [itens, calcItemDiscount]
  );



  // === Helpers de arquivo (validação e sanitização) ===

  /** Tipos MIME permitidos para anexos */
  const ALLOWED_MIME_TYPES: Record<string, string> = {
    'image/jpeg': 'image', 'image/jpg': 'image', 'image/png': 'image',
    'image/gif': 'image', 'image/webp': 'image',
    'application/pdf': 'pdf',
    'application/msword': 'document',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'document',
    'text/plain': 'document',
    'application/vnd.ms-excel': 'spreadsheet',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'spreadsheet',
  };
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  /** Sanitiza nome de arquivo removendo acentos e caracteres perigosos */
  const sanitizeFileName = (fileName: string): string => {
    const lastDot = fileName.lastIndexOf('.');
    const name = lastDot > 0 ? fileName.substring(0, lastDot) : fileName;
    const ext = lastDot > 0 ? fileName.substring(lastDot) : '';
    const sanitized = name
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '_')
      .replace(/[<>:"/\\|?*\x00-\x1F]/g, '_')
      .replace(/\.{2,}/g, '.')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
      .trim();
    return (sanitized || 'arquivo') + ext.toLowerCase();
  };

  /** Adiciona arquivos ao estado pendente com validação */
  const handleAddFiles = useCallback((files: FileList | null) => {
    if (!files) return;
    const validFiles: File[] = [];
    Array.from(files).forEach((file) => {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name}: Arquivo muito grande (máx 10MB)`);
        return;
      }
      if (!ALLOWED_MIME_TYPES[file.type]) {
        toast.error(`${file.name}: Tipo de arquivo não permitido`);
        return;
      }
      validFiles.push(file);
    });
    if (validFiles.length > 0) {
      setPendingFiles(prev => [...prev, ...validFiles]);
    }
  }, []);

  /** Remove arquivo pendente pelo índice */
  const handleRemoveFile = useCallback((index: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  /** Formata tamanho de arquivo para exibição */
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  /** Ícone do arquivo baseado no tipo MIME */
  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return ImageIcon;
    return File;
  };

  // === Submit ===

  const onSubmit = async (data: DealFormData) => {
    try {
      if (isEditing && deal) {
        // Modo edição: atualiza deal sem mexer nos itens (itens são gerenciados no modal de detalhes)
        await updateDeal.mutateAsync({
          id: deal.id,
          title: data.title,
          pipeline_id: data.pipeline_id,
          phase_id: data.phase_id,
          contact_id: data.contact_id || null,
          company_name: data.company_name || null,
          currency: data.currency,
          priority: data.priority,
          expected_close_date: data.expected_close_date || null,
          source: data.source || null,
          description: data.description || null,
          assigned_to: data.assigned_to || null,
          seller_id: data.seller_id || null,
          probability: data.probability ?? null,
        });
        // Upload de novos anexos pendentes no modo edição
        if (pendingFiles.length > 0 && currentTenant?.id) {
          const userId = (await supabase.auth.getUser()).data.user?.id;
          for (const file of pendingFiles) {
            try {
              const sanitizedName = sanitizeFileName(file.name);
              const timestamp = Date.now();
              // Path padronizado: prefixo "Deals/" para evitar pastas UUID na raiz do bucket
              const storagePath = `Deals/${currentTenant.id}/${deal.id}/${timestamp}_${sanitizedName}`;
              const { error: uploadError } = await supabase.storage
                .from('anexos')
                .upload(storagePath, file, { cacheControl: '3600', upsert: false });
              if (uploadError) {
                logger.error('Erro no upload de anexo (edição):', uploadError.message);
                continue;
              }
              const { data: urlData } = supabase.storage
                .from('anexos')
                .getPublicUrl(storagePath);
              const fileType = ALLOWED_MIME_TYPES[file.type] || 'other';
              const { error: attachError } = await supabase
                .from('crm_deal_attachments')
                .insert({
                  tenant_id: currentTenant.id,
                  deal_id: deal.id,
                  file_name: sanitizedName,
                  file_type: fileType,
                  mime_type: file.type,
                  file_size: file.size,
                  storage_path: storagePath,
                  public_url: urlData.publicUrl,
                  thumbnail_url: fileType === 'image' ? urlData.publicUrl : null,
                  uploaded_by: userId || null,
                });
              if (attachError) {
                logger.error('Erro ao registrar anexo (edição):', attachError.message);
                await supabase.storage.from('anexos').remove([storagePath]);
              }
            } catch (fileErr) {
              logger.error('Erro inesperado no upload de anexo (edição):', fileErr);
            }
          }
          queryClient.invalidateQueries({ queryKey: ['deal-attachments', deal.id] });
          logger.info('Anexos enviados no modo edição', { dealId: deal.id, count: pendingFiles.length });
          // Limpar arquivos pendentes após upload para evitar duplicação ao salvar novamente
          setPendingFiles([]);
        }
        // Sincronizar itens de produtos/serviços no modo edição (delete all + re-insert)
        if (currentTenant?.id) {
          // Remove itens antigos
          await supabase.from('crm_deal_items').delete().eq('deal_id', deal.id);
          // Re-insere os itens atuais do formulário
          if (itens.length > 0) {
            const itemsToInsert = itens.map((item, index) => ({
              tenant_id: currentTenant.id,
              deal_id: deal.id,
              product_id: item.product_id,
              quantity: item.quantidade,
              unit_price: item.valor_unitario,
              discount_percent: item.desconto_percent || 0,
              discount_fixed: item.desconto_fixed || 0,
              sort_order: index + 1,
            }));
            await supabase.from('crm_deal_items').insert(itemsToInsert);
          }
          // Sincroniza valor do deal com total dos itens
          await supabase.rpc('sync_deal_value_from_items', { p_deal_id: deal.id });
          // Invalida caches após sync para garantir valor atualizado na UI
          queryClient.invalidateQueries({ queryKey: ['crm-deal-items', deal.id] });
          queryClient.invalidateQueries({ queryKey: ['crm-deals'] });
          queryClient.invalidateQueries({ queryKey: ['crm-deal', deal.id] });
          queryClient.invalidateQueries({ queryKey: ['crm-deals-paginated'] });
        }

        // v3.1.0: sincroniza vínculos de veículos (diff entre chips e vínculos atuais)
        const selectedVehicleIds = vehicles.map((v) => v.id);
        const currentVehicleIds = linkedDealVehicles.map((v) => v.id);
        for (const vehicleId of selectedVehicleIds.filter((id) => !currentVehicleIds.includes(id))) {
          await linkDealVehicle.mutateAsync({ dealId: deal.id, vehicleId });
        }
        for (const vehicleId of currentVehicleIds.filter((id) => !selectedVehicleIds.includes(id))) {
          await linkDealVehicle.mutateAsync({ dealId: deal.id, vehicleId, unlink: true });
        }
      } else {
        // Modo criação: cria deal e insere itens
        // v3.2.0: proposta gerada automaticamente (quando houver itens)
        let createdProposal: { id: string; publicUrl: string | null } | null = null;
        const input: CreateDealInput = {
          title: data.title,
          pipeline_id: data.pipeline_id,
          phase_id: data.phase_id,
          contact_id: data.contact_id || undefined,
          company_name: data.company_name || undefined,
          currency: data.currency,
          priority: data.priority,
          expected_close_date: data.expected_close_date || undefined,
          source: data.source || undefined,
          description: data.description || undefined,
          assigned_to: data.assigned_to || undefined,
          seller_id: data.seller_id || undefined,
          probability: data.probability ?? undefined,
        };
        const result = await createDeal.mutateAsync(input);

        // Inserir itens de produtos/serviços no deal criado
        if (itens.length > 0 && currentTenant?.id) {
          const itemsToInsert = itens.map((item, index) => ({
            tenant_id: currentTenant.id,
            deal_id: result.id,
            product_id: item.product_id,
            quantity: item.quantidade,
            unit_price: item.valor_unitario,
            discount_percent: item.desconto_percent || 0,
            discount_fixed: item.desconto_fixed || 0,
            sort_order: index + 1,
          }));

          const { error: insertError } = await supabase
            .from('crm_deal_items')
            .insert(itemsToInsert);

          if (insertError) {
            logger.error('Erro ao inserir itens do deal:', insertError.message);
            toast.error('Negócio criado, mas houve erro ao adicionar os produtos.');
          } else {
            // Sincroniza valor do deal com total dos itens
            const { error: syncError } = await supabase.rpc('sync_deal_value_from_items', {
              p_deal_id: result.id,
            });
            if (syncError) {
              logger.warn('Erro ao sincronizar valor do deal:', syncError.message);
            }
            // Invalida caches relevantes
            queryClient.invalidateQueries({ queryKey: ['crm-deal-items', result.id] });
            queryClient.invalidateQueries({ queryKey: ['crm-deals'] });
            logger.info('Itens inseridos no deal com sucesso', { dealId: result.id, count: itens.length });

            // v3.2.0: proposta automática (somente com itens e quando marcado).
            // Falha aqui NÃO invalida o negócio já criado.
            if (autoCreateProposal) {
              try {
                const created = await createWithLink({
                  dealId: result.id,
                  validDays: 15,
                  // v3.7.0: tipo escolhido no bloco de proposta automática
                  proposalTypeId: proposalTypeId || null,
                });
                createdProposal = { id: created.proposalId, publicUrl: created.publicUrl };
                logger.info('Proposta gerada automaticamente', { proposalId: created.proposalId });
              } catch (proposalError: any) {
                logger.error('Erro ao gerar proposta automática:', proposalError?.message);
                // v3.9.0: trava do Workflow vira aviso modal; erro técnico segue toast.
                const exibiuAvisoWorkflow = workflowNotice.showWorkflowGuardNotice(
                  proposalError,
                  {
                    title: 'Proposta bloqueada pelo processo',
                    instruction: 'O negócio foi criado normalmente. Resolva a pendência do processo antes de gerar a proposta.',
                  },
                );
                if (!exibiuAvisoWorkflow) {
                  toast.error('Negócio criado, mas não foi possível gerar a proposta.');
                }
              }

            }
          }
        }

        // Upload de anexos pendentes após criação do deal
        if (pendingFiles.length > 0 && currentTenant?.id) {
          const userId = (await supabase.auth.getUser()).data.user?.id;
          for (const file of pendingFiles) {
            try {
              const sanitizedName = sanitizeFileName(file.name);
              const timestamp = Date.now();
              // Path padronizado: prefixo "Deals/" para evitar pastas UUID na raiz do bucket
              const storagePath = `Deals/${currentTenant.id}/${result.id}/${timestamp}_${sanitizedName}`;

              // Upload para o bucket "anexos"
              const { error: uploadError } = await supabase.storage
                .from('anexos')
                .upload(storagePath, file, { cacheControl: '3600', upsert: false });

              if (uploadError) {
                logger.error('Erro no upload de anexo:', uploadError.message);
                continue;
              }

              // Obter URL pública
              const { data: urlData } = supabase.storage
                .from('anexos')
                .getPublicUrl(storagePath);

              // Determinar tipo do arquivo
              const fileType = ALLOWED_MIME_TYPES[file.type] || 'other';

              // Registrar anexo no banco
              const { error: attachError } = await supabase
                .from('crm_deal_attachments')
                .insert({
                  tenant_id: currentTenant.id,
                  deal_id: result.id,
                  file_name: sanitizedName,
                  file_type: fileType,
                  mime_type: file.type,
                  file_size: file.size,
                  storage_path: storagePath,
                  public_url: urlData.publicUrl,
                  thumbnail_url: fileType === 'image' ? urlData.publicUrl : null,
                  uploaded_by: userId || null,
                });

              if (attachError) {
                logger.error('Erro ao registrar anexo:', attachError.message);
                await supabase.storage.from('anexos').remove([storagePath]);
              }
            } catch (fileErr) {
              logger.error('Erro inesperado no upload de anexo:', fileErr);
            }
          }
          // Invalida cache de anexos
          queryClient.invalidateQueries({ queryKey: ['deal-attachments', result.id] });
          logger.info('Anexos enviados com sucesso', { dealId: result.id, count: pendingFiles.length });
        }

        // v3.1.0: vincula os veículos selecionados ao negócio recém-criado
        for (const v of vehicles) {
          await linkDealVehicle.mutateAsync({ dealId: result.id, vehicleId: v.id });
        }

        /*
         * v3.5.0 — Exibe o modal de sucesso mantendo ESTE componente montado.
         * O fechamento real (onOpenChange(false)) só acontece quando o usuário
         * fecha o modal de sucesso; do contrário a tela que abriu o formulário
         * o desmontaria e o modal de sucesso nunca apareceria (caso do botão
         * "+" do header, que fecha o modal no onSuccess).
         */
        if (showSuccessDialog) {
          setCreatedDeal({ id: result.id, title: data.title, proposal: createdProposal });
          setSuccessOpen(true);
        } else {
          onOpenChange(false);
        }
        onSuccess?.({ id: result.id, title: data.title, proposal: createdProposal });
        return;


      }
      
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      logger.error('Erro ao salvar deal:', error);
    }
  };

  return (
    // v3.5.0: com o modal de sucesso aberto o formulário fica oculto (mas montado)
    <Dialog open={open && !successOpen && !viewDealId} onOpenChange={onOpenChange}>


      <DialogContent 
        className="max-w-5xl max-h-[90vh] flex flex-col p-0 gap-0"
        preventCloseOnOutsideInteraction
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col max-h-[90vh]">
            {/* Header — padrão CreateChargeModal */}
            <DialogHeader className="p-6 pb-4 border-b shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-primary/10">
                  {isSubmitting ? (
                    <Loader2 className="h-5 w-5 text-primary animate-spin" />
                  ) : (
                    <Briefcase className="h-5 w-5 text-primary" />
                  )}
                </div>
                <div>
                  <DialogTitle className="text-lg">
                    {isEditing ? 'Editar Negócio' : 'Novo Negócio'}
                  </DialogTitle>
                  <DialogDescription className="text-sm">
                    {isEditing 
                      ? `Atualize os dados do negócio ${formatDealCode(deal!.id)}` 
                      : 'Preencha os dados para criar um novo negócio no CRM'}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            {/* Corpo com scroll */}
            <ScrollArea className="flex-1 overflow-auto">
              <div className="p-6 space-y-6">

                {/* === SEÇÃO 1: Informações Principais === */}
                <section>
                  <div className="flex items-center gap-2 mb-3">
                    <Briefcase className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-semibold text-foreground">Informações Principais</h3>
                  </div>

                  <div className="space-y-3">
                    {/* N.Negócio + Título — mesma linha no modo edição */}
                    <div className="flex gap-3 items-end">
                      {isEditing && deal && (
                        <FormItem className="shrink-0 w-[130px]">
                          <FormLabel className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
                            # Número
                          </FormLabel>
                          <Input
                            value={formatDealCode(deal.id)}
                            readOnly
                            disabled
                            className="text-base h-11 font-mono bg-muted/50 border-border/50"
                          />
                        </FormItem>
                      )}
                      <FormField
                        control={form.control}
                        name="title"
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            {isEditing && (
                              <FormLabel className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                                Título do Negócio
                              </FormLabel>
                            )}
                            <FormControl>
                              <Input 
                                placeholder="Nome do negócio..." 
                                className="text-base h-11 border-border/50 focus-visible:ring-primary/30"
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Contato + Workspace — mesma linha 50/50 */}
                    <div className="grid grid-cols-2 gap-3">
                      {/* Contato - Campo com botão de criação */}
                      <FormField
                        control={form.control}
                        name="contact_id"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
                              <User className="w-3 h-3" />
                              Contato
                              <span className="text-destructive">*</span>
                            </FormLabel>
                            <div className="flex gap-2">
                              <FormControl className="flex-1">
                                <ContactSelector
                                  value={field.value}
                                  onSelect={field.onChange}
                                  // v3.4.0: guarda o cliente do contato para escopar os veículos
                                  onSelectContact={(contact) =>
                                    setSelectedCustomerId((contact as any)?.customer_id ?? null)
                                  }
                                />
                              </FormControl>
                              <Button
                                type="button"
                                variant="outline"
                                size="icon-sm"
                                onClick={() => setCreateContactOpen(true)}
                                title="Criar novo contato"
                                className="shrink-0"
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* Workspace */}
                      <FormField
                        control={form.control}
                        name="workspace_id"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
                              <Layers className="w-3 h-3" />
                              Workspace
                              <span className="text-destructive">*</span>
                            </FormLabel>
                            <Select value={field.value} onValueChange={field.onChange}>
                              <FormControl>
                                <SelectTrigger className="h-9 border-border/50">
                                  <SelectValue placeholder="Selecione o workspace..." />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {workspaces.map(ws => (
                                  <SelectItem key={ws.id} value={ws.id}>
                                    <div className="flex items-center gap-2">
                                      <div 
                                        className="h-3 w-3 rounded" 
                                        style={{ backgroundColor: ws.color || '#6366f1' }}
                                      />
                                      {ws.name}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Pipeline e Fase */}
                    <div className="grid grid-cols-2 gap-3">
                      <FormField
                        control={form.control}
                        name="pipeline_id"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                              Pipeline
                            </FormLabel>
                            <Select 
                              key={`pipeline-${selectedWorkspaceId}`}
                              value={field.value} 
                              onValueChange={field.onChange}
                              disabled={!selectedWorkspaceId}
                            >
                              <FormControl>
                                <SelectTrigger className="h-9 border-border/50">
                                  <SelectValue placeholder={selectedWorkspaceId ? "Selecione..." : "Selecione workspace primeiro"} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {pipelines.map(p => (
                                  <SelectItem key={p.id} value={p.id}>
                                    {p.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="phase_id"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                              Fase
                            </FormLabel>
                            <FormControl>
                              <PhaseSelector
                                key={`phase-${selectedPipelineId}`}
                                pipelineId={selectedPipelineId}
                                value={field.value}
                                onSelect={field.onChange}
                                disabled={!selectedPipelineId}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </section>

                <Separator />

                {/* === SEÇÃO 2: Produtos e Serviços === */}
                {/* Reaproveita 100% o DealItemsSummary da aba Produtos do modal de detalhes */}
                <section>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-primary" />
                      <h3 className="text-sm font-semibold text-foreground">Produtos e Serviços</h3>
                      {itens.length > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          {itens.length} {itens.length === 1 ? 'item' : 'itens'}
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-4">
                    {/* Aviso e bloqueio quando existe proposta aceita */}
                    {hasAcceptedProposal && (
                      <InlineNotice variant="warning">
                        Este negócio possui uma proposta aceita. Para alterar os produtos e
                        serviços, cancele antes a proposta aceita.
                      </InlineNotice>
                    )}

                    {/* Botão tracejado padrão DealProductsTab */}
                    {!hasAcceptedProposal && (
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full h-12 border-dashed border-2 hover:border-primary/50 hover:bg-primary/5 transition-colors"
                        onClick={() => setCatalogModalOpen(true)}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Adicionar Produto ou Serviço
                      </Button>
                    )}

                    {/* Tabela + totalizadores (separação Produtos x Serviços, MRR/Vigência) */}
                    <DealItemsSummary
                      items={itensForSummary}
                      currency="BRL"
                      readOnly={hasAcceptedProposal}
                      onUpdateQuantity={(itemId, qty) =>
                        handleUpdateItem(itemId, 'quantidade', Math.max(1, qty))
                      }
                      onUpdateDiscount={(itemId, percent, fixed) => {
                        // Sincroniza o modo de desconto local para manter cálculo consistente
                        setDiscountModes((prev) => ({
                          ...prev,
                          [itemId]: fixed > 0 ? 'fixed' : 'percent',
                        }));
                        setItens((prev) =>
                          prev.map((i) =>
                            i.id === itemId
                              ? { ...i, desconto_percent: percent, desconto_fixed: fixed }
                              : i
                          )
                        );
                      }}
                      onRemove={handleRemoveItem}
                      onOpenCatalog={() => setCatalogModalOpen(true)}
                    />

                    {/*
                      v3.2.0: geração automática da proposta ao criar o negócio.
                      Só faz sentido no modo criação e quando existem itens —
                      sem produtos/serviços não há snapshot para a proposta.
                    */}
                    {!isEditing && itens.length > 0 && (
                      <div className="rounded-lg border border-border/40 bg-muted/30 p-3 space-y-3">
                        <label className="flex items-start gap-2 cursor-pointer">
                          <Checkbox
                            checked={autoCreateProposal}
                            onCheckedChange={(checked) => setAutoCreateProposal(checked === true)}
                            className="mt-0.5"
                          />
                          <span className="flex flex-col">
                            <span className="text-sm font-medium text-foreground">
                              Gerar proposta automaticamente ao criar o negócio
                            </span>
                            <span className="text-xs text-muted-foreground">
                              Validade de 15 dias · link de compartilhamento criado junto
                            </span>
                          </span>
                        </label>

                        {/* v3.7.0: tipo da proposta a ser gerada (obrigatório) */}
                        {autoCreateProposal && (
                          <div className="space-y-1.5 pl-6">
                            <span className="text-xs font-medium text-foreground">Tipo da proposta *</span>
                            <Select value={proposalTypeId} onValueChange={setProposalTypeId}>
                              <SelectTrigger className="h-9 border-border/50">
                                <SelectValue placeholder="Selecione o tipo" />
                              </SelectTrigger>
                              <SelectContent>
                                {proposalTypes.map((t) => (
                                  <SelectItem key={t.id} value={t.id}>
                                    <span className="flex items-center gap-2">
                                      <span
                                        className="h-2.5 w-2.5 rounded-full ring-1 ring-border"
                                        style={{ backgroundColor: t.color ?? '#9ca3af' }}
                                      />
                                      {t.name}
                                    </span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {proposalTypes.length === 0 && (
                              <p className="text-xs text-muted-foreground">
                                Nenhum tipo ativo cadastrado. Cadastre em CRM › Tipos de Proposta.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </section>


                {/* Modal do catálogo de produtos */}
                <ProductCatalogModal
                  open={catalogModalOpen}
                  onOpenChange={setCatalogModalOpen}
                  dealId=""
                  currency="BRL"
                  workspaceId={selectedWorkspaceId || null}
                  dealItems={catalogItemsForModal}
                  onAddItem={handleCatalogAddItem}
                  onUpdateItemQuantity={handleCatalogUpdateQuantity}
                  onRemoveItem={handleCatalogRemoveItem}
                />

                <Separator />

                {/* === SEÇÃO 3: Detalhes do Negócio === */}
                <section>
                  <div className="flex items-center gap-2 mb-3">
                    <Users className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-semibold text-foreground">Detalhes do Negócio</h3>
                  </div>

                  <div className="space-y-3">
                    {/* Prioridade + Origem */}
                    <div className="grid grid-cols-2 gap-3 items-end">
                      <FormField
                        control={form.control}
                        name="priority"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                              Prioridade
                            </FormLabel>
                            <FormControl>
                              <PrioritySelector
                                value={field.value}
                                onChange={field.onChange}
                                compact={true}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="source"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
                              <Globe className="w-3 h-3" />
                              Origem
                              <span className="text-destructive">*</span>
                            </FormLabel>
                            <Select value={field.value || ''} onValueChange={field.onChange}>
                              <FormControl>
                                <SelectTrigger className="h-9 border-border/50">
                                  <SelectValue placeholder="Selecione..." />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {leadSources.map((source) => {
                                  const IconComponent = getLeadSourceIcon(source.id);
                                  return (
                                    <SelectItem key={source.id} value={source.id}>
                                      <div className="flex items-center gap-2">
                                        {IconComponent && <IconComponent className="h-4 w-4 text-muted-foreground" />}
                                        <span>{source.label}</span>
                                      </div>
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Responsável + Vendedor */}
                    <div className="grid grid-cols-2 gap-3">
                      <FormField
                        control={form.control}
                        name="assigned_to"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                              Responsável
                            </FormLabel>
                            <FormControl>
                              <UserSelector
                                value={field.value}
                                onSelect={field.onChange}
                                placeholder="Selecione..."
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="seller_id"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                              Vendedor
                            </FormLabel>
                            <FormControl>
                              <SellerSelector
                                value={field.value}
                                onSelect={field.onChange}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </section>

                {/* === SEÇÃO 3.1 (v3.1.0): Veículos vinculados ao negócio ===
                    v3.8.0: só é exibida quando o módulo Veículos está habilitado no tenant */}
                {vehiclesModuleEnabled && (
                <>
                <Separator />

                <section>

                  <div className="flex items-center gap-2 mb-3">
                    <Car className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-semibold text-foreground">Veículos</h3>
                    <span className="text-[10px] text-muted-foreground">(opcional)</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      {/* v3.3.0: combo de vínculo + botão "+" de cadastro rápido */}
                      <div className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <VehicleSelector
                            value={null}
                            onSelect={() => {}}
                            excludeIds={vehicles.map((v) => v.id)}
                            // v3.4.0: com a flag do tenant ligada, lista só os veículos do cliente
                            scopeCustomerId={
                              vehiclesScopeToCustomer ? selectedCustomerId : null
                            }
                            scopeContactId={
                              vehiclesScopeToCustomer ? selectedContactId || null : null
                            }
                            disabled={vehiclesScopeToCustomer && !selectedContactId}
                            onSelectVehicle={(vehicle) => {
                              // Adiciona à lista local sem duplicar (persistido no submit)
                              if (!vehicle || vehicles.some((v) => v.id === vehicle.id)) return;
                              const label =
                                [vehicle.plate, vehicle.brand_name, vehicle.model_name]
                                  .filter(Boolean)
                                  .join(' · ') || 'Veículo';
                              setVehicles((prev) => [...prev, { id: vehicle.id, label }]);
                            }}
                            placeholder={
                              vehiclesScopeToCustomer && !selectedContactId
                                ? 'Selecione um contato primeiro...'
                                : 'Adicionar veículo...'
                            }
                          />
                        </div>

                        {/* Botão "+" apenas com permissão de criar veículo */}
                        {canCreateVehicle && (
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-9 w-9 shrink-0"
                            disabled={!selectedContactId}
                            onClick={() => setVehicleFormOpen(true)}
                            aria-label="Novo veículo"
                            title={
                              selectedContactId
                                ? 'Novo veículo'
                                : 'Selecione um contato para cadastrar um veículo'
                            }
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        )}
                      </div>


                      {/* Chips dos veículos selecionados */}
                      {vehicles.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 pt-1">
                          {vehicles.map((v) => (
                            <span
                              key={v.id}
                              className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px]"
                            >
                              <Car className="w-3 h-3 text-muted-foreground" />
                              {v.label}
                              <button
                                type="button"
                                onClick={() =>
                                  setVehicles((prev) => prev.filter((x) => x.id !== v.id))
                                }
                                className="ml-0.5 text-muted-foreground hover:text-destructive"
                                aria-label={`Remover ${v.label}`}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </section>
                </>
                )}

                <Separator />



                {/* === SEÇÃO 4: Descrição e Anexos === */}
                <section>
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-semibold text-foreground">Descrição e Anexos</h3>
                    <span className="text-[10px] text-muted-foreground">(opcional)</span>
                  </div>

                  {/* Editor de texto rico */}
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <RichTextEditor
                            content={field.value || ''}
                            onChange={(html) => field.onChange(html)}
                            placeholder="Descreva os detalhes do negócio..."
                            minHeight="120px"
                            maxHeight="300px"
                            fontSize="sm"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Área de anexos */}
                  <div className="mt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-xs font-medium text-muted-foreground">
                        Anexos {(existingAttachments.length + pendingFiles.length) > 0 && `(${existingAttachments.length + pendingFiles.length})`}
                      </span>
                    </div>

                    {/* Dropzone */}
                    <div
                      className="border-2 border-dashed border-border/60 rounded-lg p-4 text-center cursor-pointer hover:border-primary/40 hover:bg-muted/20 transition-colors"
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleAddFiles(e.dataTransfer.files);
                      }}
                    >
                      <Upload className="h-6 w-6 text-muted-foreground mx-auto mb-1.5" />
                      <p className="text-xs text-muted-foreground">
                        Clique para adicionar ou arraste arquivos aqui
                      </p>
                      <p className="text-[10px] text-muted-foreground/60 mt-0.5">
                        Imagens, PDFs, documentos e planilhas — máx. 10MB por arquivo
                      </p>
                    </div>

                    {/* Input oculto */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                      className="hidden"
                      onChange={(e) => {
                        handleAddFiles(e.target.files);
                        e.target.value = ''; // Permite re-selecionar mesmo arquivo
                      }}
                    />

                    {/* Lista de anexos já salvos (modo edição) */}
                    {existingAttachments.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {existingAttachments.map((att) => {
                          const FileIconComp = att.file_type === 'image' ? ImageIcon : File;
                          return (
                            <div
                              key={att.id}
                              className="flex items-center gap-2 p-2 rounded-md bg-muted/30 border border-border/40"
                            >
                              {/* Thumbnail para imagens, ícone para outros */}
                              {att.file_type === 'image' && att.public_url ? (
                                <img 
                                  src={getStorageUrl(att.public_url) || att.public_url} 
                                  alt={att.file_name}
                                  className="h-8 w-8 rounded object-cover shrink-0"
                                />
                              ) : (
                                <FileIconComp className="h-4 w-4 text-muted-foreground shrink-0" />
                              )}
                              <span className="text-xs font-medium truncate flex-1">
                                {att.file_name}
                              </span>
                              {att.file_size && (
                                <span className="text-[10px] text-muted-foreground shrink-0">
                                  {formatFileSize(att.file_size)}
                                </span>
                              )}
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 shrink-0"
                                disabled={isDeletingAttachment}
                                onClick={() => deleteAttachment(att.id)}
                              >
                                <X className="h-3.5 w-3.5 text-destructive" />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Lista de arquivos pendentes (novos) */}
                    {pendingFiles.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {pendingFiles.map((file, index) => {
                          const FileIcon = getFileIcon(file.type);
                          return (
                            <div
                              key={`${file.name}-${index}`}
                              className="flex items-center gap-2 p-2 rounded-md bg-muted/30 border border-border/40"
                            >
                              <FileIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                              <span className="text-xs font-medium truncate flex-1">
                                {file.name}
                              </span>
                              <span className="text-[10px] text-muted-foreground shrink-0">
                                {formatFileSize(file.size)}
                              </span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 shrink-0"
                                onClick={() => handleRemoveFile(index)}
                              >
                                <X className="h-3.5 w-3.5 text-destructive" />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </section>

                <Separator />

                {/* === SEÇÃO 5: Informações Adicionais (Colapsável) === */}
                <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen}>
                  <CollapsibleTrigger className="w-full">
                    <div className="flex items-center justify-between py-1">
                      <div className="flex items-center gap-2">
                        <Settings2 className="h-4 w-4 text-muted-foreground" />
                        <h3 className="text-sm font-semibold text-foreground">Informações Adicionais</h3>
                        <span className="text-[10px] text-muted-foreground">(opcional)</span>
                      </div>
                      <ChevronDown 
                        className={cn(
                          'w-4 h-4 text-muted-foreground transition-transform duration-200',
                          detailsOpen && 'rotate-180'
                        )} 
                      />
                    </div>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent className="mt-3">
                    <div className="space-y-3">
                      {/* Data e Probabilidade */}
                      <div className="grid grid-cols-2 gap-3">
                        <FormField
                          control={form.control}
                          name="expected_close_date"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
                                <Calendar className="w-3 h-3" />
                                Data Prevista
                              </FormLabel>
                              <FormControl>
                                <Input 
                                  type="date" 
                                  className="h-9 border-border/50"
                                  {...field} 
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="probability"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
                                <Percent className="w-3 h-3" />
                                Probabilidade
                                <span className="ml-auto text-foreground font-bold text-xs">
                                  {field.value ?? 0}%
                                </span>
                              </FormLabel>
                              <FormControl>
                                <Slider
                                  min={0}
                                  max={100}
                                  step={5}
                                  value={[field.value ?? 0]}
                                  onValueChange={(values) => field.onChange(values[0])}
                                  className="mt-2"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>

              </div>
            </ScrollArea>

            {/* Footer — padrão CreateChargeModal */}
            <div className="p-4 border-t shrink-0 flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {valorTotal > 0 && (
                  <span>
                    Total: <span className="font-bold text-foreground">{formatBRL(valorTotal)}</span>
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || !form.formState.isValid}
                  className="min-w-[140px]"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {isEditing ? 'Salvando...' : 'Criando...'}
                    </>
                  ) : (
                    <>
                      <Briefcase className="h-4 w-4 mr-2" />
                      {isEditing ? 'Salvar Alterações' : 'Criar Negócio'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </DialogContent>

      {/* Modal de criação de novo contato */}
      <CreateContactDialog
        open={createContactOpen}
        onOpenChange={setCreateContactOpen}
        tenantId={currentTenant?.id || ''}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['contacts-selector'] });
          queryClient.invalidateQueries({ queryKey: ['contact-with-customer'] });
        }}
      />

      {/* v3.3.0: cadastro rápido de veículo (dono = contato selecionado) */}
      {canCreateVehicle && (
        <VehicleFormDialog
          open={vehicleFormOpen}
          onOpenChange={setVehicleFormOpen}
          scope={{ customerId: null, contactId: selectedContactId || null }}
          vehicle={null}
          onSaved={(vehicleId) => {
            // Marca para virar chip assim que a listagem do contato trouxer o rótulo
            setPendingNewVehicleId(vehicleId);
            setVehicleFormOpen(false);
          }}
        />
      )}

      {/*
        v3.5.0 — Modal de sucesso após a criação do negócio.
        Montado aqui (e não nas páginas) para aparecer em qualquer tela
        que use este formulário: header "+", chat, dashboard, modal de
        cliente, veículo, listagem e pipeline.
      */}
      {showSuccessDialog && createdDeal && (
        <DealCreatedSuccessDialog
          open={successOpen}
          onOpenChange={(isOpen) => {
            setSuccessOpen(isOpen);
            if (!isOpen) {
              setCreatedDeal(null);
              // Só agora o formulário é realmente fechado na tela de origem
              onOpenChange(false);
            }
          }}
          dealId={createdDeal.id}
          dealTitle={createdDeal.title}
          proposal={createdDeal.proposal ?? null}
          onViewDeal={() => {
            const dealId = createdDeal.id;
            setSuccessOpen(false);
            setCreatedDeal(null);
            if (onViewDeal) {
              // Tela de origem já tem seu modal premium (listagem/pipeline)
              onOpenChange(false);
              onViewDeal(dealId);
            } else {
              // v3.6.0: abre o modal padrão aqui mesmo, sem trocar de página.
              // O formulário continua montado (invisível) enquanto o modal existir.
              setViewDealId(dealId);
            }
          }}

        />
      )}

      {/*
        v3.6.0 — Modal padrão de detalhes do negócio recém-criado.
        Só é montado quando a tela de origem não informou `onViewDeal`.
      */}
      {viewDealId && (
        <DealDetailsModalPremium
          dealId={viewDealId}
          open={!!viewDealId}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              setViewDealId(null);
              // Agora sim o formulário é desmontado na tela de origem
              onOpenChange(false);
            }
          }}
          onDeleted={() => {
            setViewDealId(null);
            onOpenChange(false);
          }}
        />
      )}
    </Dialog>



  );
}
